import React from 'react';
import FormInput from "../FormInput";
import * as Yup from 'yup';
import _ from 'lodash';
import Utils from "../../common/Utils";
import AppVariables from '../../common/AppVariables';
import {VirtualCredit} from "../../common/ProxyEndpoints";
import TTLProxy from "../../common/TTLAsyncProxy";
import {useFlutterwave, FlutterWaveButton, closePaymentModal} from 'flutterwave-react-v3';
import {LoadingView} from "./LoadingView";
import HTML from "../../common/HtmlHelpers";
import uuid from 'uuid/v4';
import {Button} from "reactstrap";


function buildFlutterwaveConfig({request, amount, paymentData, farmer}) {

    return {
        public_key: AppVariables.FlutterwaveApiKey,
        tx_ref: `${request ? request.id : "TTL"}:WEB-${uuid()}`,
        amount: _.isNil(amount) ? request.cost : amount,
        currency: AppVariables.CurrencyCode,
        payment_options: 'card,mobilemoney,ussd',
        customer: {
            email: paymentData.issuer_email || farmer.email || "support@trotrotractor.com",
            phonenumber: farmer.phone,
            name: farmer.name
        },
        customizations: {
            title: 'Checkout Request',
            description: 'Payment for tractor service',
            logo: 'https://trotrotractor.blob.core.windows.net/resources/ttl_icon.png',
        }
    };
}

function FlutterwavePaymentCheckout({request, amount, farmer, paymentData, closeDialog}) {

    const fwConfig = buildFlutterwaveConfig({request, amount, paymentData, farmer});
    const handlePaymentCheckout = useFlutterwave(fwConfig);

    React.useEffect(() => {
        handlePaymentCheckout({
            callback: response => {
                if (response && response.status === "successful") {
                    HTML.toastSuccess('Payment checked out successfully');
                    closeDialog();
                    closePaymentModal();
                }
            },
            onClose: () => {
                closeDialog();
            }
        });

    }, [handlePaymentCheckout]);

    return <LoadingView isBusy message="Processing checkout..."/>
}

function FlutterwaveCheckoutButton({request, amount, onClose, onCallback}) {

    const user = TTLProxy.getInstance().getUserInfo();
    const config = buildFlutterwaveConfig({
        request,
        amount,
        paymentData: {issuer_email: user.email || (request.farmer ? request.farmer.email : null) || ""},
        farmer: {name: request.name, phone: request.phone}
    });

    const handlePaymentCheckout = useFlutterwave(config);

    return <Button block color="primary" onClick={() => handlePaymentCheckout({
        onClose: () => {
            closePaymentModal();
            onClose();
        },
        callback: (response) => {
            if (response && response.status === "successful") {
                closePaymentModal();
            }
            onCallback(response);
        }
    })}>
        <i className="fa fa-credit-card" style={{fontSize: 14}}/> Pay for tractor service
    </Button>
}

const PaymentMethods = [
    {
        id: "flutterwave",
        name: "Card, Mobile or Bank Checkout",
        logo: "/img/networks/credit_wallet_icon.png",
        roles: ["admin", "fbo", "farmer"],
        behaviours: {
            afterSubmit: {
                initializePayment: false,
                customCheckout: true
            },
            booking: {
                enabled: true,
                renderComponent: true,
                initialValues: null // Set to null to use form initial values of payment
            }
        },
        initialValues: ({farmerData, ...other}) => {

            return {
                txn_operator: "Flutterwave",
                payment_method: "flutterwave",
                txn_phone: Utils.fixPhone(farmerData.farmer ? farmerData.farmer.instance.phone : farmerData.phone, farmerData['phone-region']),
                issuer_email: (farmerData.farmer ? farmerData.farmer.instance.email : null) || "",
            }
        },
        prepareValues: ({issuer_email, ...values}) => (values),
        validationSchema: {
            issuer_email: Yup.string().email().required('Please enter email address for user making payment')
        },
        getPaymentDetails: values => ([
            {
                title: "Issuer Email",
                value: values.issuer_email
            }
        ]),
        component: ({logo, form: {errors}}) => {

            return <div style={{display: "flex", flexDirection: "column"}}>

                <FormInput name="issuer_email"
                           type="email"
                           title="Email address (Issuer)"
                           required={true}
                           icon="fa fa-envelope"
                           showPrompt={true}
                           placeholder={'Enter email address for user making payment'}/>

                <div style={{
                    padding: 8,
                    fontSize: 12,
                    backgroundColor: "#769e69",
                    borderRadius: 4,
                    color: "white",
                    textAlign: "center",
                    marginTop: 4
                }}>Confirm request to begin secured checkout <i className="fa fa-credit-card"
                                                                style={{marginLeft: 2, fontSize: 14, color: "white"}}/>
                </div>

            </div>
        },
        checkoutInfo: {
            operator: "Flutterwave",
            requestBuilderComponent: FlutterwavePaymentCheckout,
            checkoutButton: FlutterwaveCheckoutButton
        },
    },
    {
        id: "virtual-credit",
        name: "Wallet",
        logo: "/img/networks/credit_wallet_icon.png",
        roles: ["admin", "fbo", "farmer"],
        behaviours: {
            afterSubmit: {
                customCheckout: false,
                initializePayment: true,
            },
            booking: {
                enabled: true,
                renderComponent: true,
                initialValues: null // Set to null to use form initial values of payment
            }

        },
        initialValues: ({farmerData, ...other}) => {

            return {
                txn_operator: "Virtual-Credit",
                payment_method: "virtual-credit",
                txn_phone: Utils.fixPhone(farmerData.farmer ? farmerData.farmer.instance.phone : farmerData.phone, farmerData['phone-region'])
            }
        },
        validationSchema: {
            txn_phone: Yup.string().label("Registered wallet phone no.").required()
        },
        customValidation: async (values, wizardState) => {

            const {farmerData, serviceData, requestCost} = wizardState;
            const {txn_phone} = values;

            let errors = {};

            const proxy = TTLProxy.getInstance();
            if (!Utils.validatePhoneNumber(values.txn_phone) || !values.txn_phone.startsWith(AppVariables.CountryDialingCode)) {
                errors.txn_phone = "Invalid phone number";
            }

            try {

                const response = await proxy.processRequest(farmerData.farmer ? VirtualCredit.queryFarmerStatus(farmerData.farmer.instance.id) :
                    VirtualCredit.queryFarmerStatusWithPhone(txn_phone));

                const {services} = response.getPayload();
                const svc = services.find(x => x.service_id === serviceData.service.value);
                if (!svc) {
                    errors.apiResult = "Failed: Service not supported or farmer is not eligible for selected service";
                } else if (svc.amount < Number(requestCost.cost)) {
                    errors.apiResult = "Sorry, not enough funds available to complete transaction";
                }

            } catch (e) {

                if (e.response.status === 404) {
                    errors.apiResult = "Failed: Farmer not eligible for selected payment option";
                } else {
                    errors.apiResult = Utils.getResponseError(e);
                }

            }

            if (Object.keys(errors).length > 0)
                throw errors;

        },
        getPaymentDetails: values => ([
            {
                title: "Phone No.",
                value: values.txn_phone
            }
        ]),
        component: ({logo, form: {errors}}) => {

            return <React.Fragment>

                <FormInput name="txn_phone"
                           type="text"
                           title="Wallet phone no."
                           required={true}
                           disabled
                           icon="fa fa-phone"
                           showPrompt={true}
                           placeholder={`Enter wallet registered phone no. eg. ${AppVariables.CountryDialingCode}55512289`}/>

                {errors.apiResult && (<div className="error-label">{errors.apiResult}</div>)}

            </React.Fragment>
        }
    }
];

export const ExternalCheckoutProviders = PaymentMethods.filter(provider => provider.behaviours.afterSubmit.customCheckout).map(info => ({
    operator: info.checkoutInfo.operator,
    component: info.checkoutInfo.checkoutButton
}));


export default PaymentMethods;



// WEBPACK FOOTER //
// ./src/components/TractorRequestBuilder/PaymentMethods.js